/**
 * Reports - Admin reporting interface
 * Features: Product/experiment selection, report type selection, data fetching, and CSV export
 */

import { useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Alert,
  CircularProgress,
  Chip,
  Stack,
  LinearProgress,
} from '@mui/material';
import { Download, Assessment, Wifi, WifiOff } from '@mui/icons-material';
import ProductExperimentSelector from '../shared/ProductExperimentSelector';
import { useAppStore } from '../../store/appStore';
import { ReportType } from '../../types';
import { useReportJob } from '../../hooks/useReportJob';


export default function Reports() {
  const [selectedReportType, setSelectedReportType] = useState<ReportType | ''>('');
  const selectedProduct = useAppStore((state) => state.selectedProduct);
  const selectedExperiment = useAppStore((state) => state.selectedExperiment);

  // Use WebSocket-based report job hook
  const {
    jobId,
    status,
    progress,
    result: reportData,
    error,
    startReport,
    reset,
    isConnected
  } = useReportJob();

  const handleReportTypeChange = (event: any) => {
    setSelectedReportType(event.target.value);
  };

  const generateReport = async () => {
    if (selectedReportType && selectedProduct && selectedExperiment) {
      await startReport({
        type: selectedReportType,
        product: selectedProduct,
        experiment: selectedExperiment,
      });
    }
  };

  const resetReport = () => {
    reset();
  };

  const downloadCSV = () => {
    if (!reportData || !reportData.missingItems) return;

    const csvHeaders = [
      'ID',
      'Lesson Name',
      'Lesson Number',
      'Subject',
      'Grade',
      'Unit Number',
      'Unit Name',
      'Target CC Standard Codes',
      'Target Standard IDs',
      'Status',
      'Assignee',
      'Reviewers',
      'Missing Fields',
      'Created At',
      'Updated At',
    ];

    const csvRows = reportData.missingItems.map(item => [
      item.id,
      item.lesson_name,
      item.lesson_number,
      item.lesson_subject,
      item.lesson_grade.toString(),
      item.unit_number,
      item.unit_name,
      item.target_cc_standard_codes?.join('; ') || '',
      item.target_standard_ids?.join('; ') || '',
      item.review_status,
      item.assignee || '',
      item.reviewed_by?.join('; ') || '',
      item.missingFields.join('; '),
      item.created_at,
      item.updated_at,
    ]);

    const csvContent = [
      csvHeaders.join(','),
      ...csvRows.map(row => row.map(cell => `"${cell}"`).join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    if (link.download !== undefined) {
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      const environment = reportData.environment || 'DEV';
      const filename = `${selectedReportType}_${environment}_${selectedProduct}_${selectedExperiment}_${new Date().toISOString().split('T')[0]}.csv`;
      link.setAttribute('download', filename);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  const canGenerate = selectedReportType && selectedProduct && selectedExperiment && status === 'idle';
  const isGenerating = status === 'starting' || status === 'running';

  return (
    <Box>
      {/* Configuration Section */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
          <Typography variant="h6">
            Report Configuration
          </Typography>
          <Chip
            size="small"
            icon={isConnected ? <Wifi /> : <WifiOff />}
            label={isConnected ? 'Connected' : 'Disconnected'}
            color={isConnected ? 'success' : 'error'}
            variant="outlined"
          />
        </Box>

        <Stack spacing={3}>
          {/* Product/Experiment Selector */}
          <Box>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Product and Experiment
            </Typography>
            <ProductExperimentSelector />
          </Box>

          {/* Report Type Selector */}
          <FormControl fullWidth>
            <InputLabel>Report Type</InputLabel>
            <Select
              value={selectedReportType}
              onChange={handleReportTypeChange}
              label="Report Type"
            >
              <MenuItem value={ReportType.MISSING_PREDICTIONS}>
                Missing Predictions Report
              </MenuItem>
              <MenuItem value={ReportType.STATUS_BREAKDOWN} disabled>
                Status Breakdown Report (Coming Soon)
              </MenuItem>
              <MenuItem value={ReportType.REVIEWER_ACTIVITY} disabled>
                Reviewer Activity Report (Coming Soon)
              </MenuItem>
            </Select>
          </FormControl>

          {/* Generate Button */}
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
            <Button
              variant="contained"
              startIcon={<Assessment />}
              onClick={generateReport}
              disabled={!canGenerate || isGenerating}
            >
              {isGenerating ? 'Generating...' : 'Generate Report'}
            </Button>

            {(status === 'completed' || status === 'failed') && (
              <Button
                variant="outlined"
                onClick={resetReport}
              >
                Reset
              </Button>
            )}
          </Box>
        </Stack>
      </Paper>

      {/* Progress Section */}
      {isGenerating && progress && (
        <Paper sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" sx={{ mb: 2 }}>
            Generating Report
          </Typography>

          <Box sx={{ mb: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="body2" color="text.secondary">
                {progress.message}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {progress.percentage}%
              </Typography>
            </Box>
            <LinearProgress variant="determinate" value={progress.percentage} />
          </Box>

          {progress.total > 0 && (
            <Typography variant="body2" color="text.secondary">
              Progress: {progress.current.toLocaleString()} / {progress.total.toLocaleString()}
            </Typography>
          )}

          {jobId && (
            <Typography variant="caption" color="text.secondary">
              Job ID: {jobId}
            </Typography>
          )}
        </Paper>
      )}

      {/* Error Section */}
      {status === 'failed' && error && (
        <Paper sx={{ p: 3, mb: 3 }}>
          <Alert severity="error">
            Error generating report: {error}
          </Alert>
        </Paper>
      )}

      {/* Starting Section */}
      {status === 'starting' && (
        <Paper sx={{ p: 3, mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <CircularProgress size={24} />
            <Typography>Starting report generation...</Typography>
          </Box>
        </Paper>
      )}

      {status === 'completed' && reportData && (
        <Paper sx={{ p: 3 }}>
          {/* Report Summary */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Typography variant="h6">
              {selectedReportType === ReportType.MISSING_PREDICTIONS && 'Missing Predictions Report'}
            </Typography>
            <Button
              variant="outlined"
              startIcon={<Download />}
              onClick={downloadCSV}
              disabled={!reportData.missingItems?.length}
            >
              Download CSV
            </Button>
          </Box>

          {/* Summary Stats */}
          <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
            <Chip label={`Total Items: ${reportData.totalManifestItems}`} color="default" />
            <Chip
              label={`Missing: ${reportData.missingPredictions}`}
              color={reportData.missingPredictions > 0 ? 'error' : 'success'}
            />
            <Chip
              label={`${reportData.missingPercentage}% Missing`}
              color={reportData.missingPercentage > 10 ? 'error' : reportData.missingPercentage > 0 ? 'warning' : 'success'}
            />
          </Box>

          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Generated: {new Date(reportData.generatedAt).toLocaleString()} •
            Environment: {reportData.environment} •
            Product: {reportData.product} •
            Experiment: {reportData.experiment}
          </Typography>

          {/* Results Table */}
          {reportData.missingItems && reportData.missingItems.length > 0 ? (
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>ID</TableCell>
                    <TableCell>Lesson Name</TableCell>
                    <TableCell>Grade</TableCell>
                    <TableCell>Unit</TableCell>
                    <TableCell>Target CC Standards</TableCell>
                    <TableCell>Target Standard IDs</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Missing Fields</TableCell>
                    <TableCell>Assignee</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {reportData.missingItems.map((item) => (
                    <TableRow key={item.id} hover>
                      <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.85rem' }}>
                        {item.id}
                      </TableCell>
                      <TableCell>{item.lesson_name}</TableCell>
                      <TableCell>{item.lesson_grade}</TableCell>
                      <TableCell>{item.unit_name}</TableCell>
                      <TableCell>
                        {item.target_cc_standard_codes && item.target_cc_standard_codes.length > 0 ? (
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                            {item.target_cc_standard_codes.map((code, index) => (
                              <Chip
                                key={index}
                                size="small"
                                label={code}
                                color="primary"
                                variant="outlined"
                                sx={{ fontSize: '0.7rem', height: '20px' }}
                              />
                            ))}
                          </Box>
                        ) : (
                          <Typography variant="body2" color="text.secondary">-</Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        {item.target_standard_ids && item.target_standard_ids.length > 0 ? (
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                            {item.target_standard_ids.map((id, index) => (
                              <Chip
                                key={index}
                                size="small"
                                label={id.substring(0, 8) + '...'}
                                title={id}
                                color="secondary"
                                variant="outlined"
                                sx={{ fontSize: '0.7rem', height: '20px' }}
                              />
                            ))}
                          </Box>
                        ) : (
                          <Typography variant="body2" color="text.secondary">-</Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Chip
                          size="small"
                          label={item.review_status}
                          color={
                            item.review_status === 'APPROVED' ? 'success' :
                            item.review_status === 'REJECTED' ? 'error' :
                            item.review_status === 'IN_PROGRESS' ? 'warning' : 'default'
                          }
                        />
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                          {item.missingFields.map((field) => (
                            <Chip
                              key={field}
                              size="small"
                              label={field.replace('_', ' ')}
                              color="error"
                              variant="outlined"
                              sx={{ fontSize: '0.7rem' }}
                            />
                          ))}
                        </Box>
                      </TableCell>
                      <TableCell>{item.assignee || '-'}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            <Alert severity="success">
              No missing predictions found! All manifest items have their required S3 data.
            </Alert>
          )}
        </Paper>
      )}

      {status === 'idle' && (
        <Paper sx={{ p: 3 }}>
          <Typography variant="body1" color="text.secondary" textAlign="center">
            Click "Generate Report" to view results
          </Typography>
        </Paper>
      )}
    </Box>
  );
}